import { HttpErrorResponse } from '@angular/common/http';
import { Component, inject, signal } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  ReactiveFormsModule,
  ValidationErrors,
  Validators
} from '@angular/forms';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { PasswordRecoveryService } from '../../../core/auth/password-recovery.service';

function matchingPasswords(control: AbstractControl): ValidationErrors | null {
  const password = control.get('newPassword')?.value;
  const confirmation = control.get('confirmPassword')?.value;
  return password === confirmation ? null : { passwordMismatch: true };
}

@Component({
  selector: 'app-reset-password',
  standalone: true,
  imports: [ReactiveFormsModule, RouterLink],
  templateUrl: './reset-password.component.html',
  styleUrls: ['./recovery-shared.scss']
})
export class ResetPasswordComponent {
  private readonly fb = inject(FormBuilder);
  private readonly route = inject(ActivatedRoute);
  private readonly recovery = inject(PasswordRecoveryService);

  readonly loading = signal(false);
  readonly completed = signal(false);
  readonly errorMessage = signal('');
  readonly showPassword = signal(false);
  readonly token = this.route.snapshot.queryParamMap.get('token')?.trim() ?? '';

  readonly form = this.fb.nonNullable.group(
    {
      newPassword: [
        '',
        [
          Validators.required,
          Validators.minLength(12),
          Validators.maxLength(128)
        ]
      ],
      confirmPassword: ['', Validators.required]
    },
    { validators: matchingPasswords }
  );

  submit(): void {
    this.errorMessage.set('');

    if (!this.token) {
      this.errorMessage.set(
        'Este enlace de recuperacion no es valido. Solicita uno nuevo.'
      );
      return;
    }

    if (this.form.invalid || this.loading()) {
      this.form.markAllAsTouched();
      return;
    }

    this.loading.set(true);

    this.recovery
      .resetPassword(this.token, this.form.getRawValue().newPassword)
      .subscribe({
        next: () => {
          this.loading.set(false);
          this.completed.set(true);
          this.form.reset();
        },
        error: (error: HttpErrorResponse) => {
          this.loading.set(false);
          this.errorMessage.set(this.messageFor(error));
        }
      });
  }

  togglePassword(): void {
    this.showPassword.update((value) => !value);
  }

  private messageFor(error: HttpErrorResponse): string {
    const code = error.error?.failureCode as string | undefined;

    if (code === 'PASSWORD_TOO_SHORT') {
      return 'La contrasena debe tener al menos 12 caracteres.';
    }

    if (code === 'PASSWORD_TOO_LONG') {
      return 'La contrasena no puede superar 128 caracteres.';
    }

    if (error.status === 429) {
      return 'Hay demasiados intentos. Espera un momento e intenta nuevamente.';
    }

    return 'El enlace no es valido, ya fue utilizado o ha expirado. Solicita uno nuevo.';
  }
}